import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Code2, Package, Rocket, Settings, FileText, Copy, Check, Trash2, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore';
import { useAccountStore } from '../../stores/useAccountStore';
import { useWalletStore } from '../../stores/useWalletStore';
import { usePrefabStore } from '../../stores/usePrefabStore';
import { rivellumApi } from '../../api/rivellumApi';
import type { RegisteredModule } from '../../types/project';
import s from './Pages.module.css';

type ProjectTab = 'overview' | 'code' | 'prefabs' | 'deployments' | 'integrations' | 'settings';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openProject = useProjectStore(p => p.openProject);
  const activeProject = useProjectStore(p => p.activeProject);
  const isLoading = useProjectStore(p => p.isLoading);
  const removePrefabFromProject = useProjectStore(p => p.removePrefabFromProject);
  const registerModule = useProjectStore(p => p.registerModule);
  const setRegistrationStatus = useProjectStore(p => p.setRegistrationStatus);
  const deployments = useAccountStore(a => a.deployments);
  const addDeployment = useAccountStore(a => a.addDeployment);
  const signIntent = useWalletStore(w => w.signIntent);
  const walletStatus = useWalletStore(w => w.connectionStatus);
  const walletAccount = useWalletStore(w => w.activeAccount);
  const walletConnect = useWalletStore(w => w.connect);
  const prefabCatalog = usePrefabStore(ps => ps.catalog);
  const fetchCatalog = usePrefabStore(ps => ps.fetchCatalog);
  const [tab, setTab] = useState<ProjectTab>('overview');
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [integrationLang, setIntegrationLang] = useState<string>('typescript');
  const [registeringPrefab, setRegisteringPrefab] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    if (id) openProject(id);
  }, [id, openProject]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  if (isLoading) {
    return (
      <div className={s.page}>
        <div className={s.skeleton} style={{ width: 200, height: 28, marginBottom: 16 }} />
        <div className={s.skeleton} style={{ width: 300, height: 16, marginBottom: 32 }} />
        <div className={s.statsRow}>
          {[1, 2, 3, 4].map(i => <div key={i} className={`${s.skeleton} ${s.skeletonCard}`} />)}
        </div>
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyTitle}>Project not found</div>
          <button className={s.btnPrimary} onClick={() => navigate('/projects')}>Back to Projects</button>
        </div>
      </div>
    );
  }

  const projectDeployments = deployments.filter(d => d.projectId === activeProject.id);
  const addedPrefabs = activeProject.addedPrefabs ?? [];
  const registeredModules = activeProject.registeredModules ?? [];

  const isModuleRegistered = (prefabId: string) =>
    registeredModules.some(m => m.prefabId === prefabId);

  const hasAnyRegistered = registeredModules.length > 0;

  const handleRegisterPrefab = async (prefabId: string, label: string, initArgs: unknown[] = []) => {
    // Auto-connect wallet if not connected
    let account = walletAccount;
    if (walletStatus !== 'connected' || !account) {
      const connected = await walletConnect();
      account = useWalletStore.getState().activeAccount;
      if (!connected || !account) {
        setRegisterError('Could not connect wallet. Please ensure Natos Wallet is unlocked.');
        return;
      }
    }

    setRegisteringPrefab(prefabId);
    setRegisterError(null);
    try {
      const registrationIntent: Record<string, unknown> = {
        sender: account.address,
        recipient: '0x0000000000000000000000000000000000000000',
        amount: 0,
        memo: `register:${prefabId}`,
        data: JSON.stringify({
          type: 'prefab_register',
          prefab_id: prefabId,
          project_name: activeProject.name,
          network: 'testnet',
          fee_rivl_display: '1.0000 RIVL',
          fee_usd_display: '$1.00',
        }),
      };

      const signed = await signIntent(registrationIntent);
      if (!signed) {
        const walletError = useWalletStore.getState().error;
        setRegisterError(walletError ?? 'Transaction was rejected by wallet.');
        return;
      }

      // Ensure we have a project ID — register the batch if first time
      let projectId = activeProject.registeredProjectId;
      if (!projectId) {
        const allPrefabs = addedPrefabs.map(p => ({
          prefab_id: p.prefabId,
          label: p.label,
          init_args: (p.initArgs ?? []) as unknown[],
        }));
        const res = await rivellumApi.registerProject(activeProject.name, allPrefabs, account.address);
        projectId = res.project_id;
        await setRegistrationStatus('registered', res.project_id);
      }

      // Register this individual prefab
      const res = await rivellumApi.registerPrefab(
        projectId,
        activeProject.name,
        prefabId,
        label,
        initArgs,
        account.address,
      );

      const mod: RegisteredModule = {
        prefabId: res.module.prefab_id,
        label: res.module.label,
        codeHash: res.module.code_hash,
        intentId: res.module.intent_id,
        contractAddress: res.module.contract_id,
        registeredAt: Date.now(),
      };

      await registerModule(prefabId, mod);

      addDeployment({
        id: res.module.intent_id || crypto.randomUUID(),
        projectId: activeProject.id,
        projectName: `${activeProject.name} / ${label}`,
        txHash: res.module.intent_id,
        network: 'testnet',
        status: 'submitted',
        timestamp: Date.now(),
        contractAddress: res.module.contract_id ?? res.module.code_hash,
        category: 'prefab',
      });
    } catch (e) {
      setRegisterError(e instanceof Error ? e.message : 'Registration failed');
    } finally {
      setRegisteringPrefab(null);
    }
  };

  const tabs: { key: ProjectTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <FileText size={14} /> },
    { key: 'code', label: 'Code', icon: <Code2 size={14} /> },
    { key: 'prefabs', label: `Modules (${addedPrefabs.length})`, icon: <Package size={14} /> },
    { key: 'deployments', label: 'Deployments', icon: <Rocket size={14} /> },
    { key: 'integrations', label: 'Integrations', icon: <Copy size={14} /> },
    { key: 'settings', label: 'Settings', icon: <Settings size={14} /> },
  ];

  return (
    <div className={s.page}>
      <button className={s.btnGhost} onClick={() => navigate('/projects')} style={{ marginBottom: 'var(--spacing-lg)' }}>
        <ArrowLeft size={16} /> Projects
      </button>

      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>{activeProject.name}</h1>
        <p className={s.pageSubtitle}>
          {activeProject.source === 'prefab' ? 'Created from prefab' : 'Custom project'} · {activeProject.files.length} files · Updated {new Date(activeProject.updatedAt).toLocaleDateString()}
        </p>
      </div>

      {/* Tabs */}
      <div className={s.tabs}>
        {tabs.map(t => (
          <button
            key={t.key}
            className={`${s.tab} ${tab === t.key ? s.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon}
            <span style={{ marginLeft: 6 }}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* TAB: Overview */}
      {tab === 'overview' && (
        <div>
          <div className={s.statsRow}>
            <div className={s.statCard}>
              <div className={s.statLabel}>Modules</div>
              <div className={s.statValue}>{addedPrefabs.length}</div>
            </div>
            <div className={s.statCard}>
              <div className={s.statLabel}>Registered</div>
              <div className={s.statValue}>{registeredModules.length}</div>
            </div>
            <div className={s.statCard}>
              <div className={s.statLabel}>Files</div>
              <div className={s.statValue}>{activeProject.files.length}</div>
            </div>
            <div className={s.statCard}>
              <div className={s.statLabel}>Project ID</div>
              <div className={s.statValue} style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                {activeProject.registeredProjectId ? activeProject.registeredProjectId.slice(0, 12) + '...' : '—'}
              </div>
            </div>
          </div>

          {/* Module list with per-module register buttons */}
          {addedPrefabs.length > 0 && (
            <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 'var(--spacing-xl)' }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Prefab ID</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {addedPrefabs.map(p => {
                    const registered = isModuleRegistered(p.prefabId);
                    const regMod = registeredModules.find(m => m.prefabId === p.prefabId);
                    const isRegistering = registeringPrefab === p.prefabId;
                    return (
                      <tr key={p.prefabId}>
                        <td style={{ fontWeight: 500 }}>{p.label}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.prefabId}</td>
                        <td>
                          {registered ? (
                            <span className={`${s.badge} ${s.badgeSuccess}`}>
                              <ShieldCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
                              Registered
                            </span>
                          ) : (
                            <span className={`${s.badge} ${s.badgeNeutral}`}>Not registered</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {registered ? (
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                              {regMod?.contractAddress?.slice(0, 10)}...
                            </span>
                          ) : (
                            <button
                              className={`${s.btnPrimary} ${s.btnSmall}`}
                              onClick={() => handleRegisterPrefab(p.prefabId, p.label, p.initArgs)}
                              disabled={isRegistering}
                              style={{ padding: '4px 12px', fontSize: 12 }}
                            >
                              {isRegistering ? <Loader2 size={12} className={s.spin} /> : <Rocket size={12} />}
                              <span style={{ marginLeft: 4 }}>Register</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button className={s.btnPrimary} onClick={() => navigate('/prefabs')}>
              <Package size={16} /> Add Prefab Module
            </button>
            {activeProject.files.length > 0 && (
              <button className={s.btnSecondary} onClick={() => navigate(`/ide/${activeProject.id}`)}>
                <Code2 size={16} /> Open in IDE
              </button>
            )}
          </div>

          {registerError && (
            <div style={{ padding: 'var(--spacing-md)', background: 'rgba(248,81,73,0.1)', border: '1px solid var(--color-danger)', borderRadius: 'var(--radius-md)', marginTop: 'var(--spacing-lg)', fontSize: 13, color: 'var(--color-danger)' }}>
              <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              {registerError}
            </div>
          )}
        </div>
      )}

      {/* TAB: Code */}
      {tab === 'code' && (
        <div>
          {activeProject.files.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyTitle}>No files yet</div>
              <div className={s.emptyDesc}>Open this project in the IDE to start writing code.</div>
              <button className={s.btnPrimary} onClick={() => navigate(`/ide/${activeProject.id}`)}>
                <Code2 size={16} /> Open in IDE
              </button>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Language</th>
                      <th>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeProject.files.map(f => (
                      <tr key={f.id}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{f.path}</td>
                        <td><span className={`${s.badge} ${s.badgeNeutral}`}>{f.language}</span></td>
                        <td style={{ color: 'var(--color-text-tertiary)' }}>{f.content.length} chars</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <button className={s.btnPrimary} onClick={() => navigate(`/ide/${activeProject.id}`)}>
                  <Code2 size={16} /> Edit in IDE
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB: Modules — args and customizations */}
      {tab === 'prefabs' && (
        <div>
          {addedPrefabs.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}><Package size={40} /></div>
              <div className={s.emptyTitle}>No prefab modules added</div>
              <div className={s.emptyDesc}>
                Browse the prefab catalog and add modules to this project.
              </div>
              <button className={s.btnPrimary} onClick={() => navigate('/prefabs')}>
                <Package size={16} /> Browse Prefabs
              </button>
            </div>
          ) : (
            <>
              {addedPrefabs.map(p => {
                const registered = isModuleRegistered(p.prefabId);
                const regMod = registeredModules.find(m => m.prefabId === p.prefabId);
                const prefab = prefabCatalog.find(c => c.id === p.prefabId);
                const configEntries = Object.entries(p.configValues ?? {});

                return (
                  <div key={p.prefabId} style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-lg)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-md)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Package size={16} style={{ color: 'var(--color-accent)' }} />
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{p.label}</h3>
                        <span className={`${s.badge} ${s.badgeNeutral}`} style={{ fontSize: 10 }}>{p.prefabId}</span>
                        {registered && (
                          <span className={`${s.badge} ${s.badgeSuccess}`}>
                            <ShieldCheck size={12} style={{ marginRight: 3, verticalAlign: -1 }} /> Registered
                          </span>
                        )}
                      </div>
                      {!registered && (
                        <button
                          className={`${s.btnGhost} ${s.btnSmall}`}
                          onClick={async () => {
                            await removePrefabFromProject(p.prefabId);
                            if (id) openProject(id);
                          }}
                          title="Remove module"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    {prefab?.description && (
                      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 var(--spacing-md) 0' }}>
                        {prefab.description}
                      </p>
                    )}

                    {/* Init args */}
                    {p.initArgs.length > 0 && (
                      <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Init Arguments</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, padding: '8px 12px', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                          {p.initArgs.map((arg, i) => (
                            <div key={i} style={{ color: 'var(--color-text-secondary)' }}>
                              arg[{i}]: {JSON.stringify(arg)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Config values */}
                    {configEntries.length > 0 && (
                      <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Configuration</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, padding: '8px 12px', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                          {configEntries.map(([key, val]) => (
                            <div key={key} style={{ display: 'contents' }}>
                              <span style={{ color: 'var(--color-text-tertiary)' }}>{key}:</span>
                              <span>{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Registration info */}
                    {registered && regMod && (
                      <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', color: 'var(--color-text-secondary)' }}>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>Contract:</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{regMod.contractAddress}</span>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>Code Hash:</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{regMod.codeHash?.slice(0, 16)}...</span>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>Intent:</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{regMod.intentId?.slice(0, 16)}...</span>
                        {regMod.registeredAt && (
                          <>
                            <span style={{ color: 'var(--color-text-tertiary)' }}>Registered:</span>
                            <span>{new Date(regMod.registeredAt).toLocaleString()}</span>
                          </>
                        )}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 8 }}>
                      Added {new Date(p.addedAt).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}

              <button className={s.btnSecondary} onClick={() => navigate('/prefabs')}>
                <Package size={16} /> Add More Modules
              </button>
            </>
          )}
        </div>
      )}

      {/* TAB: Deployments */}
      {tab === 'deployments' && (
        <div>
          {projectDeployments.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}><Rocket size={40} /></div>
              <div className={s.emptyTitle}>No deployments yet</div>
              <div className={s.emptyDesc}>Deploy custom code or a prefab to see it here.</div>
            </div>
          ) : (
            <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Network</th>
                    <th>Category</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {projectDeployments.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500 }}>{d.projectName}</td>
                      <td>
                        <span className={`${s.badge} ${d.status === 'finalized' ? s.badgeSuccess : d.status === 'failed' ? s.badgeDanger : s.badgeWarning}`}>
                          {d.status}
                        </span>
                      </td>
                      <td><span className={`${s.badge} ${s.badgeInfo}`}>{d.network}</span></td>
                      <td><span className={`${s.badge} ${s.badgeNeutral}`}>{d.category ?? 'custom'}</span></td>
                      <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>{new Date(d.timestamp).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB: Integrations */}
      {tab === 'integrations' && (
        <IntegrationsTab
          project={activeProject}
          prefabCatalog={prefabCatalog}
          lang={integrationLang}
          setLang={setIntegrationLang}
          copiedSnippet={copiedSnippet}
          onCopy={(key, code) => {
            navigator.clipboard.writeText(code);
            setCopiedSnippet(key);
            setTimeout(() => setCopiedSnippet(null), 2000);
          }}
        />
      )}

      {/* TAB: Settings */}
      {tab === 'settings' && (
        <div style={{ maxWidth: 480 }}>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Project Name</label>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, padding: '10px 14px', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
              {activeProject.name}
            </div>
          </div>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Project ID</label>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '10px 14px', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', color: 'var(--color-text-tertiary)' }}>
              {activeProject.id}
            </div>
          </div>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Created</label>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {new Date(activeProject.createdAt).toLocaleString()}
            </div>
          </div>
          {hasAnyRegistered ? (
            <div style={{ padding: 'var(--spacing-md)', background: 'rgba(139,148,158,0.1)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginTop: 'var(--spacing-xl)', fontSize: 13, color: 'var(--color-text-secondary)' }}>
              <ShieldCheck size={14} style={{ marginRight: 6, verticalAlign: -2, color: 'var(--color-success)' }} />
              This project has registered modules on chain and cannot be deleted.
            </div>
          ) : (
            <div style={{ marginTop: 'var(--spacing-xl)', paddingTop: 'var(--spacing-lg)', borderTop: '1px solid var(--color-border)' }}>
              <button className={s.btnGhost} style={{ color: 'var(--color-danger)' }} onClick={() => { useProjectStore.getState().deleteProject(activeProject.id); navigate('/projects'); }}>
                <Trash2 size={14} /> Delete Project
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Integration Snippets Component ────────────────────────────

const LANGS = [
  { key: 'typescript', label: 'TypeScript' },
  { key: 'python', label: 'Python' },
  { key: 'rust', label: 'Rust' },
  { key: 'go', label: 'Go' },
  { key: 'csharp', label: 'C# (Unity)' },
  { key: 'gdscript', label: 'GDScript' },
  { key: 'unreal', label: 'C++ (Unreal)' },
  { key: 'curl', label: 'cURL' },
];

function mapParamType(_kind: string, type: string, lang: string): string {
  const m: Record<string, Record<string, string>> = {
    typescript: { u64: 'number', u128: 'bigint', address: 'string', bytes: 'string', bool: 'boolean' },
    python: { u64: 'int', u128: 'int', address: 'str', bytes: 'str', bool: 'bool' },
    rust: { u64: 'u64', u128: 'u128', address: '&str', bytes: '&[u8]', bool: 'bool' },
    go: { u64: 'uint64', u128: 'string', address: 'string', bytes: '[]byte', bool: 'bool' },
    csharp: { u64: 'ulong', u128: 'string', address: 'string', bytes: 'byte[]', bool: 'bool' },
    gdscript: { u64: 'int', u128: 'int', address: 'String', bytes: 'String', bool: 'bool' },
    unreal: { u64: 'uint64', u128: 'FString', address: 'FString', bytes: 'TArray<uint8>', bool: 'bool' },
    curl: { u64: 'number', u128: 'string', address: 'string', bytes: 'string', bool: 'boolean' },
  };
  return m[lang]?.[type] ?? type;
}

function exampleValue(type: string): string {
  switch (type) {
    case 'u64': case 'u128': return '100';
    case 'address': return '"0xabc...def"';
    case 'bytes': return '"hello"';
    case 'bool': return 'true';
    default: return '"..."';
  }
}

interface SnippetGenOpts {
  contractAddr: string;
  moduleName: string;
  actionName: string;
  params: { name: string; type: string }[];
  network: string;
}

const DEFAULT_SNIPPET_NODE_URL = 'https://rpc.rivellum.network';

function genSnippet(lang: string, o: SnippetGenOpts): string {
  const paramObj = o.params.map(p => `    ${p.name}: ${exampleValue(p.type)}`).join(',\n');
  const paramArgs = o.params.map(p => `${p.name}: ${mapParamType('primitive', p.type, lang)}`).join(', ');

  switch (lang) {
    case 'typescript':
      return `import { RivellumClient } from '@rivellum/sdk';

const client = new RivellumClient({ nodeUrl: '${o.network}' });
const ${o.moduleName} = client.contract('${o.contractAddr}');

// ${o.actionName}(${o.params.map(p => p.name).join(', ')})
const result = await ${o.moduleName}.call('${o.actionName}', {
${paramObj}
});
console.log(result.receipt_id);`;

    case 'python':
      return `from rivellum_ai import RivellumClient

client = RivellumClient(node_url="${o.network}")
${o.moduleName} = client.contract("${o.contractAddr}")

# ${o.actionName}(${o.params.map(p => p.name).join(', ')})
result = await ${o.moduleName}.call("${o.actionName}", {
${o.params.map(p => `    "${p.name}": ${exampleValue(p.type)}`).join(',\n')}
})
print(result["receipt_id"])`;

    case 'rust':
      return `use rivellum_sdk::RivellumClient;

let client = RivellumClient::new("${o.network}");
let ${o.moduleName} = client.contract("${o.contractAddr}");

// ${o.actionName}(${paramArgs})
let result = ${o.moduleName}.call("${o.actionName}", serde_json::json!({
${o.params.map(p => `    "${p.name}": ${exampleValue(p.type)}`).join(',\n')}
})).await?;
println!("{}", result.receipt_id);`;

    case 'go':
      return `import rivellum "github.com/rivellumlabs/rivellum-go-sdk"

client := rivellum.NewClient("${o.network}")
${o.moduleName} := client.Contract("${o.contractAddr}")

// ${o.actionName}(${o.params.map(p => p.name).join(', ')})
result, err := ${o.moduleName}.Call("${o.actionName}", map[string]interface{}{
${o.params.map(p => `    "${p.name}": ${exampleValue(p.type)},`).join('\n')}
})
fmt.Println(result.ReceiptID)`;

    case 'csharp':
      return `using Rivellum.SDK;
using System.Collections.Generic;

var config = new RivellumClientConfig { rpcUrl = "${o.network}" };
var client = new RivellumClient(config);

// ${o.actionName}(${paramArgs})
var result = await client.Execute("${o.contractAddr}", "${o.actionName}",
    new List<object> { ${o.params.map(p => exampleValue(p.type)).join(', ')} });
Debug.Log(result.txHash);`;

    case 'gdscript':
      return `var config = RivellumClientConfig.new()
config.node_url = "${o.network}"
var logger = RivellumLogger.new()
var rpc = RpcClient.new(config, logger)

# ${o.actionName}(${o.params.map(p => p.name).join(', ')})
var result = await rpc.post_json("/v1/mist/call/${o.contractAddr}", {
    "function": "${o.actionName}",
    "args": {
${o.params.map(p => `        "${p.name}": ${exampleValue(p.type)}`).join(',\n')}
    }
})
print(result)`;

    case 'unreal':
      return `#include "RivellumClient.h"

FRivellumClientConfig Config;
Config.NodeUrl = TEXT("${o.network}");

FRivellumClient Client(Config);
const FString Response = Client.PostRawJson(
  TEXT("/v1/mist/call/${o.contractAddr}"),
  TEXT(R"JSON({
    "function": "${o.actionName}",
    "args": {
${o.params.map(p => `      "${p.name}": ${exampleValue(p.type)}`).join(',\n')}
    }
  })JSON")
);

UE_LOG(LogTemp, Display, TEXT("%s"), *Response);`;

    case 'curl':
      return `curl -X POST ${o.network}/v1/mist/call/${o.contractAddr} \\
  -H "Content-Type: application/json" \\
  -d '{
    "function": "${o.actionName}",
    "args": {
${o.params.map(p => `      "${p.name}": ${exampleValue(p.type)}`).join(',\n')}
    }
  }'`;

    default:
      return '';
  }
}

interface IntegrationsProps {
  project: import('../../types/project').Project;
  prefabCatalog: import('../../types/prefab').PrefabDefinition[];
  lang: string;
  setLang: (lang: string) => void;
  copiedSnippet: string | null;
  onCopy: (key: string, code: string) => void;
}

function IntegrationsTab({ project, prefabCatalog, lang, setLang, copiedSnippet, onCopy }: IntegrationsProps) {
  const modules = project.registeredModules ?? [];

  if (modules.length === 0) {
    return (
      <div className={s.emptyState}>
        <div className={s.emptyIcon}><Code2 size={40} /></div>
        <div className={s.emptyTitle}>No integration snippets yet</div>
        <div className={s.emptyDesc}>Register and deploy your project to generate per-module integration code.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Language selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 'var(--spacing-xl)' }}>
        {LANGS.map(l => (
          <button
            key={l.key}
            className={`${s.tab} ${lang === l.key ? s.tabActive : ''}`}
            onClick={() => setLang(l.key)}
            style={{ padding: '6px 14px', fontSize: 12 }}
          >
            {l.label}
          </button>
        ))}
      </div>

      {modules.map(mod => {
        const prefab = prefabCatalog.find(p => p.id === mod.prefabId);
        const actions = (prefab?.actions ?? []).filter(a => a.visibility === 'public' && a.name !== 'init');
        const contractAddr = mod.contractAddress || mod.codeHash;
        const modVarName = mod.label.replace(/[^a-zA-Z0-9]/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'contract';

        return (
          <div key={mod.prefabId} style={{ marginBottom: 'var(--spacing-2xl)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--spacing-md)' }}>
              <Package size={16} style={{ color: 'var(--color-accent)' }} />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{mod.label}</h3>
              <span className={`${s.badge} ${s.badgeNeutral}`} style={{ fontSize: 10 }}>{mod.prefabId}</span>
            </div>

            {actions.length === 0 ? (
              <div className={s.codeBlock} style={{ marginBottom: 'var(--spacing-md)' }}>
                <div className={s.codeHeader}>
                  <span>Connect — {LANGS.find(l => l.key === lang)?.label}</span>
                  <button className={`${s.btnGhost} ${s.btnSmall}`} onClick={() => {
                    const code = genSnippet(lang, { contractAddr, moduleName: modVarName, actionName: 'example', params: [], network: DEFAULT_SNIPPET_NODE_URL });
                    onCopy(`${mod.prefabId}-connect`, code);
                  }}>
                    {copiedSnippet === `${mod.prefabId}-connect` ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
                <pre className={s.codeContent} style={{ margin: 0, fontSize: 12, lineHeight: 1.6 }}>
                  {genSnippet(lang, { contractAddr, moduleName: modVarName, actionName: 'example', params: [], network: DEFAULT_SNIPPET_NODE_URL })}
                </pre>
              </div>
            ) : (
              actions.map(action => {
                const snippetKey = `${mod.prefabId}-${action.name}`;
                const params = action.parameters.map(p => ({ name: p.name, type: p.type.type }));
                const code = genSnippet(lang, { contractAddr, moduleName: modVarName, actionName: action.name, params, network: DEFAULT_SNIPPET_NODE_URL });
                return (
                  <div key={action.name} className={s.codeBlock} style={{ marginBottom: 'var(--spacing-md)' }}>
                    <div className={s.codeHeader}>
                      <span>
                        <strong>{action.name}</strong>
                        {action.description ? ` — ${action.description}` : ''}
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          ({action.parameters.map(p => `${p.name}: ${p.type.type}`).join(', ')})
                        </span>
                      </span>
                      <button className={`${s.btnGhost} ${s.btnSmall}`} onClick={() => onCopy(snippetKey, code)}>
                        {copiedSnippet === snippetKey ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                    </div>
                    <pre className={s.codeContent} style={{ margin: 0, fontSize: 12, lineHeight: 1.6 }}>{code}</pre>
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
}
